import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from './local-storage.service';
import { StorageKeys } from '../config';

@Injectable({
	providedIn: 'root',
})
export class LanguageService {
	private readonly supportedLanguage: string[] = ['tr', 'en'];

	constructor(
		private readonly translateService: TranslateService,
		private readonly localStorageService: LocalStorageService
	) {
		translateService.addLangs(this.getLanguageList());
		translateService.setDefaultLang(this.getDefaultLanguage());
		this.translateService.use(this.getUserLanguage()).subscribe();
	}

	public getLanguageList(): string[] {
		return this.supportedLanguage.slice(0);
	}

	private getDefaultLanguage(): string {
		return this.supportedLanguage[0];
	}

	private getBrowserLanguage(): string | undefined {
		return this.translateService.getBrowserLang();
	}

	private isValidKey(language: string | undefined): boolean {
		if (!language) return false;
		return this.getLanguageList().indexOf(language) !== -1;
	}

	public getCurrentLanguage(): string {
		return this.translateService.currentLang;
	}

	private getUserLanguage(): string {
		const storedLang = this.localStorageService.getItem(StorageKeys.USER_LANGUAGE);

		if (storedLang) {
			// there is a language key store in the local storage
			if (this.isValidKey(storedLang)) {
				// check if the value is supported
				return storedLang;
			} else {
				return this.getDefaultLanguage();
			}
		} else {
			// get the browser language, no language key is found in the local storage
			const browserLang = this.getBrowserLanguage();
			if (this.isValidKey(browserLang)) {
				return browserLang!;
			} else {
				return this.getDefaultLanguage();
			}
		}
	}

	/**
	 * Set the translation language and save the value in local storage
	 * This method is called after login and when user changes their language from user profile
	 */
	public setTranslationLanguage(lang: string): void {
		this.translateService.use(lang).subscribe(() => {
			this.localStorageService.setItem(StorageKeys.USER_LANGUAGE, lang);
		});
	}

	/**
	 * Configure supported languages (optional, for applications that need different languages)
	 * Call this before initialization if you need custom language support
	 */
	public setSupportedLanguages(languages: string[]): void {
		this.supportedLanguage.length = 0;
		this.supportedLanguage.push(...languages);
		this.translateService.addLangs(languages);
	}
}
